
import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../App';
import { Send, Search, MoreVertical, AlertTriangle, ArrowLeft } from 'lucide-react';
import { checkContentSafety } from '../services/geminiService';
import { User } from '../types';

const Chat: React.FC = () => {
    const { currentUser, messages, sendMessage, markAsRead, allUsers } = useAppContext();
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
    const [newMessage, setNewMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // --- Dynamic Contact List Logic ---
    // 1. Get unique IDs of people I've chatted with
    const chatHistoryIds = Array.from(new Set(
        messages
            .filter(m => m.senderId === currentUser?.id || m.receiverId === currentUser?.id)
            .map(m => m.senderId === currentUser?.id ? m.receiverId : m.senderId)
    ));

    // 2. Get my connections
    const connectionIds = currentUser?.connections || [];

    // 3. Merge and deduplicate
    const allContactIds = Array.from(new Set([...chatHistoryIds, ...connectionIds]));

    // 4. Map to User objects
    let contacts = allContactIds
        .map(id => allUsers.find(u => u.id === id))
        .filter(u => u !== undefined) as User[];

    // 5. If searching, include ANY user from allUsers that matches
    if (searchTerm.trim()) {
        const lowerSearch = searchTerm.toLowerCase();
        // If search is active, show anyone matching the name, even if not in history
        contacts = allUsers.filter(u => 
            u.id !== currentUser?.id && 
            u.name.toLowerCase().includes(lowerSearch)
        );
    } else {
        // Sort by most recent message if not searching
        contacts.sort((a, b) => {
            const lastMsgA = messages
                .filter(m => (m.senderId === a.id || m.receiverId === a.id))
                .sort((x, y) => y.timestamp - x.timestamp)[0];
            const lastMsgB = messages
                .filter(m => (m.senderId === b.id || m.receiverId === b.id))
                .sort((x, y) => y.timestamp - x.timestamp)[0];
            
            return (lastMsgB?.timestamp || 0) - (lastMsgA?.timestamp || 0);
        });
    }

    const activeContact = allUsers.find(u => u.id === selectedContactId);
    const activeContactAvatar = activeContact?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(activeContact?.name || 'Unknown')}&background=random`;

    // Filter messages for current conversation
    const conversation = messages.filter(
        m => (m.senderId === currentUser?.id && m.receiverId === selectedContactId) ||
             (m.senderId === selectedContactId && m.receiverId === currentUser?.id)
    ).sort((a, b) => a.timestamp - b.timestamp);

    // Scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
        // Mark as read when opening conversation or when new messages arrive
        if (selectedContactId) {
            markAsRead(selectedContactId);
        }
    }, [conversation.length, selectedContactId]);

    const handleSend = async () => {
        if (!newMessage.trim() || !selectedContactId) return;
        setError(null);
        
        // Safety Check
        const check = await checkContentSafety(newMessage);
        if(!check.safe) {
            setError(`Message flagged: ${check.reason || "Inappropriate content"}`);
            return;
        }

        sendMessage(selectedContactId, newMessage);
        setNewMessage('');
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-[calc(100vh-12rem)] md:h-[calc(100vh-8rem)] flex overflow-hidden">
            {/* Sidebar - Hidden on mobile if contact selected */}
            <div className={`flex-col bg-gray-50 border-r border-gray-200 w-full md:w-80 ${selectedContactId ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-gray-200 bg-white">
                    <h2 className="text-xl font-bold text-gray-900 mb-3">Messages</h2>
                    <div className="relative">
                        <input 
                            type="text" 
                            placeholder="Search people..." 
                            className="w-full pl-9 pr-3 py-2 bg-transparent border-b border-gray-300 rounded-none text-sm focus:outline-none focus:border-blue-600 transition-colors placeholder-gray-400"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-2.5" />
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                    {contacts.length === 0 && (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            {searchTerm ? "No users found." : "No messages yet. Search for a friend to start chatting!"}
                        </div>
                    )}
                    {contacts.map(contact => {
                         const lastMsg = messages
                            .filter(m => (m.senderId === contact.id && m.receiverId === currentUser?.id) || (m.senderId === currentUser?.id && m.receiverId === contact.id))
                            .sort((a,b) => b.timestamp - a.timestamp)[0];
                         const unread = messages.filter(m => m.senderId === contact.id && m.receiverId === currentUser?.id && !m.isRead).length;
                         const contactAvatar = contact.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&background=random`;

                         return (
                            <div 
                                key={contact.id}
                                onClick={() => setSelectedContactId(contact.id)}
                                className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-100 transition ${selectedContactId === contact.id ? 'bg-blue-50 border-r-4 border-blue-600' : ''}`}
                            >
                                <div className="relative">
                                    <img src={contactAvatar} alt={contact.name} className="w-12 h-12 rounded-full object-cover bg-gray-200" />
                                    {unread > 0 && <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 border-2 border-white rounded-full"></span>}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-baseline mb-1">
                                        <h3 className={`text-sm font-semibold truncate ${selectedContactId === contact.id ? 'text-blue-900' : 'text-gray-900'}`}>{contact.name}</h3>
                                        <span className="text-xs text-gray-400">
                                            {lastMsg ? new Date(lastMsg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                        </span>
                                    </div>
                                    <p className={`text-xs truncate ${unread > 0 ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                                        {lastMsg ? (lastMsg.senderId === currentUser?.id ? `You: ${lastMsg.content}` : lastMsg.content) : <span className="italic text-gray-400">Start a conversation</span>}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Chat Area - Full screen on mobile if selected */}
            <div className={`flex-1 flex-col bg-white ${selectedContactId ? 'flex' : 'hidden md:flex'}`}>
                {selectedContactId ? (
                    <>
                        {/* Chat Header */}
                        <div className="h-16 border-b border-gray-200 flex justify-between items-center px-4 md:px-6 bg-white shrink-0">
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => setSelectedContactId(null)}
                                    className="md:hidden p-1 -ml-2 text-gray-500 hover:bg-gray-100 rounded-full"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                {activeContact && <img src={activeContactAvatar} className="w-10 h-10 rounded-full bg-gray-200 object-cover" alt="" />}
                                <div className="min-w-0">
                                    <h3 className="font-bold text-gray-900 truncate max-w-[150px] sm:max-w-none">{activeContact?.name || "Loading..."}</h3>
                                    <span className="text-xs text-gray-500 flex items-center gap-1 truncate">
                                        {activeContact?.voluntaryCall || 'Member'}
                                    </span>
                                </div>
                            </div>
                            <button className="text-gray-400 hover:text-gray-600">
                                <MoreVertical className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Messages List */}
                        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 bg-gray-50">
                            {conversation.length === 0 && (
                                <div className="text-center text-gray-400 text-sm py-10">
                                    Send a message to start chatting with {activeContact?.name || 'this user'}.
                                </div>
                            )}
                            {conversation.map(msg => {
                                const isMe = msg.senderId === currentUser?.id;
                                return (
                                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] md:max-w-[70%] rounded-2xl px-4 py-3 shadow-sm ${
                                            isMe 
                                            ? 'bg-blue-600 text-white rounded-tr-none' 
                                            : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none'
                                        }`}>
                                            <p className="text-sm leading-relaxed">{msg.content}</p>
                                            <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                                                {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="p-3 md:p-4 bg-white border-t border-gray-200">
                            {error && (
                                <div className="mb-2 text-xs text-red-600 flex items-center gap-1 bg-red-50 p-1.5 rounded">
                                    <AlertTriangle className="w-3 h-3"/> {error}
                                </div>
                            )}
                            <div className="flex items-center gap-2 bg-transparent border-b border-gray-300 px-2 py-2 focus-within:border-blue-600 transition-colors">
                                <input 
                                    type="text" 
                                    className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-2 placeholder-gray-400"
                                    placeholder={`Message...`}
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                />
                                <button 
                                    onClick={handleSend}
                                    disabled={!newMessage.trim()}
                                    className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Send className="w-8 h-8 text-gray-300" />
                        </div>
                        <p>Select a contact to start messaging</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Chat;
